import {
  trackCotacaoApresentacaoUpdate,
  trackCotacaoCupomAplicado,
  trackCotacaoPaymentStatus,
  trackCotacaoPurchase,
  trackCotacaoPurchaseError,
} from "@/analytics/tracking";
import {
  ClientInfoData,
  PaymentFormValues,
} from "@/components/pages/ecommerce/payment/payment-types";
import {
  aplicarCupom,
  buscarCupons,
  calcularColetaEntrega,
  cotacaoId,
  fazerCotacao,
  updateCotacaoField,
} from "@/services/flowQuote";
import {
  checkPaymentStatusAPI,
  getFaturasCotacao,
  payWithBoletoAPI,
  payWithCreditCardAPI,
  payWithPixAPI,
} from "@/services/payment";
import { CotacaoEcommerce } from "@/types/Cotacao";
import { CreateQuote, CreateQuoteResponse, Cupom } from "@/types/FlowQuote";
import {
  BoletoPaymentResponse,
  CreditCardPaymentResponse,
  FaturaCotacao,
  PixPaymentResponse,
} from "@/types/Payment";
import { formatDateToBr, formatDateToYMD } from "@/utils/format";
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

interface VehicleData {
  plate: string;
  chassis: string;
  model: string;
  year: string;
  color: string;
}

interface VehicleCotation {
  brand?: string;
  value?: string;
  isWorking?: string;
  isArmored?: string;
}

interface TransportData {
  originLocation?: string;
  destinationLocation?: string;
  cepOriginLocation?: string;
  cepDestinationLocation?: string;
}

export interface PersonalData {
  name?: string;
  email?: string;
  ddi?: string;
  phone?: string;
  reason?: string;
  transportSchedule?: string;
}

interface UtmParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

interface QuotePriceDetail {
  base: number;
  pickup: number;
  delivery: number;
  insurancePremium: number;
  discount: number;
  subtotal: number;
  total: number;
}

interface QuoteModifiers {
  coleta?: boolean;
  entrega?: boolean;
  operante?: boolean;
  blindado?: boolean;
  seguro?: boolean;
  date?: Date;
  express?: boolean;
}

interface QuoteContextType {
  cotacao: CotacaoEcommerce | null;
  vehicleCotation: VehicleCotation;
  transportData: TransportData;
  personalData: PersonalData;
  quotePrice: QuotePriceDetail;
  quoteModifiers: QuoteModifiers;
  utmParameters: UtmParameters;
  errorCotacao: boolean;
  addDiscount: (discount: number) => void;
  addAdditionals: (additionals: number) => void;
  updateVehicleCotation: (data: Partial<VehicleCotation>) => void;
  updateTransportData: (data: Partial<TransportData>) => void;
  updatePersonalData: (data: Partial<PersonalData>) => void;
  updateUtmParameters: (data: Partial<UtmParameters>) => void;
  clearUtmParameters: () => void;
  sendQuote: (
    data: Partial<PersonalData>,
  ) => Promise<CreateQuoteResponse | null>;
  updateQuoteModifiers: (data: Partial<QuoteModifiers>) => void;
  fetchCotacaoById: (cve_codigo: string, cve_token?: string) => Promise<void>;
  updateVehicleData: (data: VehicleData) => void;
  updateModifierColeta: (value: boolean) => Promise<void>;
  updateModifierEntrega: (value: boolean) => Promise<void>;
  updateModifierBlindado: (value: boolean) => Promise<void>;
  updateModifierOperante: (value: boolean) => Promise<void>;
  updateModifierSeguro: (value: boolean) => Promise<void>;
  updateModifierExpress: (value: boolean) => Promise<void>;
  updateDateEntrega: (date: Date) => Promise<void>;
  updateCEPEntrega: (cep: string) => Promise<void>;
  updateCEPColeta: (cep: string) => Promise<void>;

  cuponsDisponiveis: Cupom[];
  fetchCupons: () => Promise<void>;
  applyCoupon: (cupom: Cupom) => Promise<boolean>;
  calculateValue: (cotacao: CotacaoEcommerce, typePayment?: string) => void;

  isContractSigned: boolean;

  clientInfoData: ClientInfoData | null;
  saveClientInfo: (data: ClientInfoData) => void;

  paymentData: PaymentFormValues | null;
  savePaymentData: (data: PaymentFormValues) => void;
  // paymentLocked: boolean;
  // setPaymentLocked: (value: boolean) => void;

  payWithCreditCard: (
    data: PaymentFormValues,
  ) => Promise<CreditCardPaymentResponse>;
  payWithBoleto: (data: PaymentFormValues) => Promise<BoletoPaymentResponse>;
  payWithPix: (data: PaymentFormValues) => Promise<PixPaymentResponse>;
  checkPaymentStatus: (
    transactionId: string,
  ) => Promise<"approved" | "pending" | "rejected">;
  transactionId: string | null;

  transaction: FaturaCotacao[];
  setTransaction: (faturas: FaturaCotacao[]) => void;
  refetchTransaction: () => Promise<FaturaCotacao[]>;

  // Persisted payment data
  persistedBoletoData: BoletoPaymentResponse | null;
  persistedPixData: PixPaymentResponse | null;
  persistedCreditCardData: CreditCardPaymentResponse | null;
  saveBoletoData: (data: BoletoPaymentResponse | null) => void;
  savePixData: (data: PixPaymentResponse | null) => void;
  saveCreditCardData: (data: CreditCardPaymentResponse | null) => void;
  clearPersistedPaymentData: () => void;

  token: string | null;
  saveToken: (token: string) => void;
  getToken: () => string | null;
  clearToken: () => void;
}

const QuoteContext = createContext<QuoteContextType | undefined>(undefined);

interface QuoteProviderProps {
  children: ReactNode;
}

export const QuoteProvider: React.FC<QuoteProviderProps> = ({ children }) => {
  const [errorCotacao, setErrorCotacao] = useState(false);
  const [cotacao, setCotacao] = useState<CotacaoEcommerce | null>(null);
  const [vehicleCotation, setVehicleCotation] = useState<VehicleCotation>({});
  const [transportData, setTransportData] = useState<TransportData>({});
  const [personalData, setPersonalData] = useState<PersonalData>({});
  const [quoteModifiers, setQuoteModifiers] = useState<QuoteModifiers>({});
  const [cuponsDisponiveis, setCuponsDisponiveis] = useState<Cupom[]>([]);
  const [clientInfoData, setClientInfoData] = useState<ClientInfoData | null>(
    null,
  );
  const saveClientInfo = (data: ClientInfoData) => setClientInfoData(data);
  const isContractSigned = !!cotacao?.cve_data_contrato;

  const [paymentData, setPaymentData] = useState<PaymentFormValues | null>(
    null,
  );

  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<FaturaCotacao[]>([]);
  const [persistedBoletoData, setPersistedBoletoData] =
    useState<BoletoPaymentResponse | null>(null);
  const [persistedPixData, setPersistedPixData] =
    useState<PixPaymentResponse | null>(null);
  const [persistedCreditCardData, setPersistedCreditCardData] =
    useState<CreditCardPaymentResponse | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem("quote-token") || null;
  });
  const [utmParameters, setUtmParameters] = useState<UtmParameters>(() => {
    const stored = sessionStorage.getItem("nacional-utm-params");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {};
      }
    }
    return {};
  });
  const [quotePrice, setQuotePrice] = useState<QuotePriceDetail>({
    base: 0,
    pickup: 0,
    delivery: 0,
    insurancePremium: 0,
    discount: 0,
    subtotal: 0,
    total: 0,
  });

  const updateVehicleCotation = (data: Partial<VehicleCotation>) => {
    setVehicleCotation((prev) => ({ ...prev, ...data }));
  };

  const updateTransportData = (data: Partial<TransportData>) => {
    setTransportData((prev) => ({ ...prev, ...data }));
  };

  const updatePersonalData = (data: Partial<PersonalData>) => {
    setPersonalData((prev) => ({ ...prev, ...data }));
  };

  const updateUtmParameters = useCallback((data: Partial<UtmParameters>) => {
    setUtmParameters((prev) => {
      const isEqual = Object.keys(data).every(
        (key) =>
          prev[key as keyof UtmParameters] === data[key as keyof UtmParameters],
      );

      if (isEqual && Object.keys(prev).length === Object.keys(data).length) {
        return prev;
      }

      const newParams = { ...prev, ...data };

      sessionStorage.setItem("nacional-utm-params", JSON.stringify(newParams));

      return newParams;
    });
  }, []);

  const clearUtmParameters = useCallback(() => {
    setUtmParameters({});
    sessionStorage.removeItem("nacional-utm-params");
    sessionStorage.removeItem("nacional-utm-processed");
  }, []);

  const updateQuoteModifiers = (data: Partial<QuoteModifiers>) => {
    setQuoteModifiers((prev) => ({ ...prev, ...data }));
  };

  const savePaymentData = (data: PaymentFormValues) => {
    setPaymentData(data);
  };

  const saveBoletoData = (data: BoletoPaymentResponse | null) => {
    setPersistedBoletoData(data);
  };

  const savePixData = (data: PixPaymentResponse | null) => {
    setPersistedPixData(data);
  };

  const saveCreditCardData = (data: CreditCardPaymentResponse | null) => {
    setPersistedCreditCardData(data);
  };

  const clearPersistedPaymentData = () => {
    setPersistedBoletoData(null);
    setPersistedPixData(null);
    setPersistedCreditCardData(null);
  };

  const saveToken = (tokenValue: string) => {
    setToken(tokenValue);
    localStorage.setItem("quote-token", tokenValue);
  };

  const getToken = (): string | null => {
    return token || localStorage.getItem("quote-token");
  };

  const clearToken = () => {
    setToken(null);
    localStorage.removeItem("quote-token");
  };

  const addDiscount = (discount: number) => {
    if (discount < 0) {
      console.error("Discount cannot be negative");
      return;
    }
    const valuePorcent = (discount / 100) * quotePrice.base;
    setQuotePrice((prev) => ({
      ...prev,
      discount: valuePorcent,
      subtotal:
        prev.base +
        prev.pickup +
        prev.delivery +
        prev.insurancePremium -
        valuePorcent,
      total:
        prev.base +
        prev.pickup +
        prev.delivery +
        prev.insurancePremium -
        valuePorcent,
    }));
  };

  const addAdditionals = (additionals: number) => {
    setQuotePrice((prev) => ({
      ...prev,
      pickup: prev.pickup + additionals,
      subtotal:
        prev.base +
        prev.pickup +
        additionals +
        prev.delivery +
        prev.insurancePremium -
        prev.discount,
      total:
        prev.base +
        prev.pickup +
        additionals +
        prev.delivery +
        prev.insurancePremium -
        prev.discount,
    }));
  };

  const fetchCupons = async () => {
    try {
      const cupons = await buscarCupons();
      setCuponsDisponiveis(cupons);
    } catch (error) {
      console.error("Erro ao buscar cupons:", error);
    }
  };

  const applyCoupon = async (cupom: Cupom): Promise<boolean> => {
    if (!cotacao) return false;

    const status = await aplicarCupom(cupom.cup_codigo, cotacao.ctc_codigo);

    if (status === "Sucesso") {
      await fetchCotacaoById(cotacao.cve_codigo);
      if (cotacao) {
        trackCotacaoCupomAplicado(
          {
            ...cotacao,
          } as CotacaoEcommerce,
          true,
          {
            codigo: cupom.cup_codigo,
            nome: cupom.cup_nome,
            valor: Number(cupom.cup_valor),
            tipo: cupom.cup_valor_tipo,
          },
        );

        trackCotacaoApresentacaoUpdate(cotacao, "coupon_applied", {
          code: cupom.cup_codigo,
          type: cupom.cup_valor_tipo,
          value: cupom.cup_valor,
        });
      }
      return true;
    } else {
      return false;
    }
  };

  const calculateValue = (cotacao: CotacaoEcommerce, typePayment?: string) => {
    let base = parseFloat(cotacao.cve_preco_cotacao || "0");

    const seguro_base = parseFloat(cotacao.cve_valor_seguro || "0");
    const seguro_adicional =
      parseFloat(cotacao.cve_valor_cobertura_seguro || "0") - seguro_base;
    const coleta = parseFloat(cotacao.cve_valor_coleta || "0");
    const entrega = parseFloat(cotacao.cve_valor_entrega || "0");
    const acressimo = parseFloat(cotacao.cve_valor_acrescimo || "0");
    const acressimo_veiculo = parseFloat(
      cotacao.cve_valor_acrescimo_veiculo || "0",
    );

    const descontoCotacao = parseFloat(cotacao.cve_valor_desconto || "0");
    const valorCupom = parseFloat(cotacao.cup_valor || "0");
    const cupomValido = isNaN(valorCupom) ? 0 : valorCupom;

    const pickupValue = cotacao.tro_codigo === "2" ? coleta : 0;
    const deliveryValue = cotacao.trd_codigo === "2" ? entrega : 0;
    const insurancePremiumValue =
      cotacao.cve_tipo_seguro === "2" ? seguro_adicional : 0;

    const additionalCharges = acressimo + acressimo_veiculo + seguro_base;

    const subtotalBeforeDiscount =
      base +
      pickupValue +
      deliveryValue +
      insurancePremiumValue +
      additionalCharges;

    let descontoCupom = 0;
    if (cotacao.cup_valor_tipo === "PERCENTUAL") {
      descontoCupom = (base * cupomValido) / 100;
    } else if (cotacao.cup_valor_tipo === "FIXO") {
      descontoCupom = cupomValido;
    }

    const descontoSoma =
      typePayment === "credit" ? 0 : descontoCotacao + descontoCupom;
    const desconto = isNaN(descontoSoma) ? 0 : descontoSoma;

    const subtotal = subtotalBeforeDiscount - desconto;

    base = base + additionalCharges - desconto;

    setQuotePrice({
      base,
      pickup: pickupValue,
      delivery: deliveryValue,
      insurancePremium: insurancePremiumValue,
      discount: desconto,
      subtotal,
      total: subtotal,
    });
  };

  const fetchCotacaoById = async (cve_codigo: string, cve_token?: string) => {
    try {
      const currentToken = cve_token || getToken();

      if (!currentToken) {
        throw new Error("Token é obrigatório para acessar a cotação");
      }

      const data = await cotacaoId(cve_codigo, currentToken);
      const cotacao = data[0];

      clearPersistedPaymentData();
      setTransaction([]);

      setCotacao(cotacao);
      setQuoteModifiers({
        coleta: cotacao.tro_codigo === "2",
        entrega: cotacao.trd_codigo === "2",
        blindado: cotacao.cve_blindado === "1",
        operante: cotacao.cve_operante === "1",
        seguro: cotacao.cve_tipo_seguro === "2",
        express: cotacao.cve_express_codigo === "1",
      });
      calculateValue(cotacao);
    } catch (error) {
      setErrorCotacao(true);
      console.error("Erro ao sincronizar cotação:", error);
    }
  };

  const updateModifierColeta = async (value: boolean) => {
    if (isContractSigned) return;
    try {
      await updateCotacaoField(
        cotacao.cve_codigo,
        "tro_codigo",
        value ? "2" : "1",
      );
    } catch {
      /* logged in service */
    }
    await fetchCotacaoById(cotacao.cve_codigo);
    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "coleta_toggle", {
        enabled: value,
      });
    }
  };

  const updateModifierEntrega = async (value: boolean) => {
    if (isContractSigned) return;
    try {
      await updateCotacaoField(
        cotacao.cve_codigo,
        "trd_codigo",
        value ? "2" : "1",
      );
    } catch {
      /* logged in service */
    }
    await fetchCotacaoById(cotacao.cve_codigo);
    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "entrega_toggle", {
        enabled: value,
      });
    }
  };

  const updateModifierBlindado = async (value: boolean) => {
    if (isContractSigned) return;
    try {
      await updateCotacaoField(
        cotacao.cve_codigo,
        "cve_blindado",
        value ? "1" : "0",
      );
    } catch {
      /* logged in service */
    }
    await fetchCotacaoById(cotacao.cve_codigo);
    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "blindado_toggle", {
        enabled: value,
      });
    }
  };

  const updateModifierOperante = async (value: boolean) => {
    if (isContractSigned) return;
    try {
      await updateCotacaoField(
        cotacao.cve_codigo,
        "cve_operante",
        value ? "1" : "0",
      );
      if (!value && cotacao.cve_tipo_seguro === "2") {
        await updateCotacaoField(cotacao.cve_codigo, "cve_tipo_seguro", "0");
      }
    } catch {
      /* logged in service */
    }
    await fetchCotacaoById(cotacao.cve_codigo);
    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "operante_toggle", {
        enabled: value,
      });
    }
  };

  const updateModifierSeguro = async (value: boolean) => {
    if (isContractSigned) return;
    try {
      await updateCotacaoField(
        cotacao.cve_codigo,
        "cve_tipo_seguro",
        value ? "2" : "0",
      );
    } catch {
      /* logged in service */
    }
    await fetchCotacaoById(cotacao.cve_codigo);
    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "seguro_toggle", {
        enabled: value,
      });
    }
  };

  const updateModifierExpress = async (value: boolean) => {
    if (isContractSigned) return;
    try {
      await updateCotacaoField(
        cotacao.cve_codigo,
        "cve_express_codigo",
        value ? "1" : "0",
      );
    } catch {
      /* logged in service */
    }
    await fetchCotacaoById(cotacao.cve_codigo);
    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "express_toggle", {
        enabled: value,
      });
    }
  };

  const updateDateEntrega = async (date: Date) => {
    if (isContractSigned) return;
    try {
      await updateCotacaoField(
        cotacao.cve_codigo,
        "cve_data_previsao_recebimento",
        formatDateToYMD(date),
      );
    } catch {
      /* logged in service */
    }
    await fetchCotacaoById(cotacao.cve_codigo);
    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "date_changed", {
        new_date: formatDateToYMD(date),
      });
    }
  };

  const updateCEPEntrega = async (cep: string): Promise<void> => {
    if (isContractSigned) return;
    await calcularColetaEntrega(
      cotacao.cve_codigo,
      cotacao.bop_codigo_destino,
      cep,
      "entrega",
    );
    await fetchCotacaoById(cotacao.cve_codigo);
    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "cep_entrega_changed", { cep });
    }
  };

  const updateCEPColeta = async (cep: string): Promise<void> => {
    if (isContractSigned) return;
    await calcularColetaEntrega(
      cotacao.cve_codigo,
      cotacao.bop_codigo_origem,
      cep,
      "coleta",
    );
    await fetchCotacaoById(cotacao.cve_codigo);
    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "cep_coleta_changed", { cep });
    }
  };

  const updateVehicleData = async (data: VehicleData) => {
    if (!cotacao) return;

    try {
      await Promise.all([
        updateCotacaoField(cotacao.cve_codigo, "cve_placa", data.plate),
        updateCotacaoField(cotacao.cve_codigo, "cve_chassi", data.chassis),
        updateCotacaoField(cotacao.cve_codigo, "vmd_descricao", data.model),
        updateCotacaoField(cotacao.cve_codigo, "cve_ano_mod", data.year),
        updateCotacaoField(cotacao.cve_codigo, "cor_descricao", data.color),
      ]);
    } catch {
      /* logged in service */
    }

    await fetchCotacaoById(cotacao.cve_codigo);

    if (cotacao) {
      trackCotacaoApresentacaoUpdate(cotacao, "vehicle_updated", {
        ...data,
      });
    }
  };

  const sendQuote = async (
    data: Partial<PersonalData>,
  ): Promise<CreateQuoteResponse | null> => {
    updatePersonalData(data);
    try {
      if (
        !vehicleCotation.brand ||
        !vehicleCotation.value ||
        !data.transportSchedule ||
        !vehicleCotation.isWorking ||
        !vehicleCotation.isArmored ||
        !data.name ||
        !data.email ||
        !data.phone ||
        !transportData.originLocation ||
        !transportData.destinationLocation ||
        !data.reason
      ) {
        console.error("Dados da cotação incompletos");
        return null;
      }

      const payload: CreateQuote = {
        mun_origem_codigo: Number(transportData.originLocation),
        mun_destino_codigo: Number(transportData.destinationLocation),
        veiculo_codigo: Number(vehicleCotation.brand),
        veiculo_valor: Number(vehicleCotation.value) / 100,
        data_transporte: formatDateToBr(data.transportSchedule),
        veiculo_funcionando: vehicleCotation.isWorking === "true" ? 1 : 0,
        veiculo_blindado: vehicleCotation.isArmored === "true" ? 1 : 0,
        nome_completo: data.name,
        whatsapp: data.ddi + data.phone,
        email: data.email,
        motivo_codigo: Number(data.reason),
        cve_coleta_cep: transportData?.cepOriginLocation,
        cve_entrega_cep: transportData?.cepDestinationLocation,
      };

      const hasUtmParams = Object.keys(utmParameters).some(
        (key) => utmParameters[key as keyof UtmParameters],
      );

      if (hasUtmParams) {
        payload.gets_json = { ...utmParameters };
      }

      const response = await fazerCotacao(payload);

      saveToken(response.cve_token);
      clearPersistedPaymentData();
      setTransaction([]);

      if (hasUtmParams) {
        clearUtmParameters();
      }

      return response;
    } catch (error: unknown) {
      const err = error as { response: { data: { error: string } } };
      throw Error(err.response.data.error);
    }
  };

  function requiresVerification(error: unknown): boolean {
    const axiosError = error as { response?: { status?: number } };
    const status = axiosError?.response?.status;

    if (status && status >= 400 && status < 500) return false;
    return true;
  }

  function parsePaymentErrorMessage(error: unknown): string {
    const err = error as {
      response?: { data?: { message?: unknown } };
      message?: string;
    };
    const apiError = err?.response?.data;
    if (!apiError) return err?.message || "Ocorreu um erro inesperado.";

    if (typeof apiError.message === "string") {
      try {
        const parsed = JSON.parse(apiError.message);
        if (Array.isArray(parsed)) {
          return (
            parsed.join(" | ") || "Ocorreu um erro ao processar o pagamento."
          );
        }
        if (typeof parsed === "object" && parsed !== null) {
          return Object.entries(parsed)
            .map(
              ([field, msgs]) =>
                `${field}: ${Array.isArray(msgs) ? (msgs as string[]).join(", ") : msgs}`,
            )
            .join(" | ");
        }
      } catch {
        /* ignore JSON parse errors */
      }
      return apiError.message;
    }

    if (typeof apiError.message === "object" && apiError.message !== null) {
      return Object.entries(apiError.message as Record<string, unknown>)
        .map(([field, messages]) => {
          const msg = Array.isArray(messages)
            ? messages.join(", ")
            : String(messages);
          return `${field}: ${msg}`;
        })
        .join(" | ");
    }

    return "Ocorreu um erro ao processar o pagamento.";
  }

  const normalizeNumeroEndereco = (numero: string) =>
    /^[Ss]\/?[Nn]$/.test(numero.trim()) ? "0" : numero;

  const payWithCreditCard = async (data: PaymentFormValues) => {
    if (data.paymentMethod !== "credit") {
      throw new Error("Forma de pagamento não é cartão de crédito");
    }

    if (!cotacao) {
      throw new Error("Cotação não encontrada");
    }

    const { card1, personalInfo, installments, address } = data;
    try {
      const response = await payWithCreditCardAPI({
        cotacao_codigo: Number(cotacao.cve_codigo),
        cpf: personalInfo.cpf.replace(/\D/g, ""),
        nome: card1.name,
        sobrenome: personalInfo.name,
        email: cotacao.ctc_email,
        telefone: (cotacao.ctc_telefone_1 || "").replace(/\D/g, ""),
        numero: card1.number,
        cvv: card1.cvv,
        mes: card1.expiry.split("/")[0],
        ano: `20${card1.expiry.split("/")[1]}`,
        parcelas: Number(installments),
        endereco: address.logradouro,
        numero_endereco: normalizeNumeroEndereco(address.numero),
        complemento: address.complemento || "",
        bairro: address.bairro,
        cidade: address.cidade || "",
        estado: address.estado || "",
        cep: address.cep.replace("-", ""),
        mun_codigo: address.mun_codigo,
      });
      setTransactionId(response.data.invoice_id);
      if (cotacao) {
        trackCotacaoPurchase(cotacao, {
          transaction_id: response.data.invoice_id,
          method: "credit",
          installments: Number(installments),
          brand: undefined,
          last4: (card1.number || "").slice(-4),
        });
      }
      return response;
    } catch (error: unknown) {
      const errorMessage = parsePaymentErrorMessage(error);
      if (cotacao)
        trackCotacaoPurchaseError(cotacao, "credit", { message: errorMessage });
      const paymentError = new Error(errorMessage);
      paymentError.name = "PaymentError";
      const axiosError = error as {
        response?: { data?: unknown; status?: number };
      };
      Object.assign(paymentError, {
        rawBackendResponse: axiosError.response?.data,
        httpStatus: axiosError.response?.status,
        needsVerification: requiresVerification(error),
      });
      throw paymentError;
    }
  };

  const payWithPix = async (data: PaymentFormValues) => {
    if (data.paymentMethod !== "pix") {
      throw new Error("Forma de pagamento não é PIX");
    }

    if (!cotacao) {
      throw new Error("Cotação não encontrada");
    }

    const { personalInfo, address } = data;

    try {
      const response = await payWithPixAPI({
        cotacao_codigo: Number(cotacao.cve_codigo),
        cpf: personalInfo.cpf.replace(/\D/g, ""),
        nome: personalInfo.name,
        email: cotacao.ctc_email,
        telefone: (cotacao.ctc_telefone_1 || "").replace(/\D/g, ""),
        endereco: address.logradouro,
        numero_endereco: normalizeNumeroEndereco(address.numero),
        complemento: address.complemento || "",
        bairro: address.bairro,
        cidade: address.cidade || "",
        estado: address.estado || "",
        cep: address.cep.replace("-", ""),
        mun_codigo: address.mun_codigo,
      });

      if (!response.success) {
        const err = new Error(response.message || "Erro no pagamento via PIX");
        Object.assign(err, { pixResponse: response });
        throw err;
      }

      setTransactionId(response.data.invoice_id);
      if (cotacao) {
        trackCotacaoPurchase(cotacao, {
          transaction_id: response.data.invoice_id,
          method: "pix",
          pix_expires_at: response.data?.due_date,
          pix_qr_code: response.data?.pix.qrcode,
        });
      }

      return response;
    } catch (error: unknown) {
      const errorMessage = parsePaymentErrorMessage(error);
      trackCotacaoPurchaseError(cotacao, "pix", { message: errorMessage });
      const paymentError = new Error(errorMessage);
      paymentError.name = "PaymentError";
      const axiosError = error as {
        response?: { data?: unknown; status?: number };
        pixResponse?: unknown;
      };
      Object.assign(paymentError, {
        rawBackendResponse: axiosError.response?.data ?? axiosError.pixResponse,
        httpStatus: axiosError.response?.status,
        needsVerification: requiresVerification(error),
      });
      throw paymentError;
    }
  };

  const payWithBoleto = async (data: PaymentFormValues) => {
    if (data.paymentMethod !== "bank") {
      throw new Error("Forma de pagamento não é boleto");
    }

    if (!cotacao) {
      throw new Error("Cotação não encontrada");
    }

    const { personalInfo, address } = data;

    try {
      const response = await payWithBoletoAPI({
        cotacao_codigo: Number(cotacao.cve_codigo),
        cpf: personalInfo.cpf.replace(/\D/g, ""),
        nome: personalInfo.name,
        email: cotacao.ctc_email,
        telefone: (cotacao.ctc_telefone_1 || "").replace(/\D/g, ""),
        endereco: address.logradouro,
        numero_endereco: normalizeNumeroEndereco(address.numero),
        complemento: address.complemento || "",
        bairro: address.bairro,
        cidade: address.cidade || "",
        estado: address.estado || "",
        cep: address.cep.replace("-", ""),
        mun_codigo: address.mun_codigo,
      });

      if (!response.success) {
        const err = new Error(
          response.message || "Erro no pagamento via boleto",
        );
        Object.assign(err, { boletoResponse: response });
        throw err;
      }

      setTransactionId(response.data.invoice_id);
      if (cotacao) {
        trackCotacaoPurchase(cotacao, {
          transaction_id: response.data.invoice_id,
          method: "bank",
          boleto_url: response.data?.bank_slip_url,
          boleto_due_date: response.data?.due_date,
        });
      }
      return response;
    } catch (error: unknown) {
      const errorMessage = parsePaymentErrorMessage(error);
      trackCotacaoPurchaseError(cotacao, "bank", { message: errorMessage });
      const paymentError = new Error(errorMessage);
      paymentError.name = "PaymentError";
      const axiosError = error as {
        response?: { data?: unknown; status?: number };
        boletoResponse?: unknown;
      };
      Object.assign(paymentError, {
        rawBackendResponse:
          axiosError.response?.data ?? axiosError.boletoResponse,
        httpStatus: axiosError.response?.status,
        needsVerification: requiresVerification(error),
      });
      throw paymentError;
    }
  };

  const refetchTransaction = async (): Promise<FaturaCotacao[]> => {
    if (!cotacao) return [];
    try {
      const faturas = await getFaturasCotacao(cotacao.cve_codigo);
      setTransaction(faturas);
      return faturas;
    } catch {
      return transaction;
    }
  };

  const checkPaymentStatus = async (invoiceId: string) => {
    const response = await checkPaymentStatusAPI(invoiceId);

    if (!response.success) {
      throw new Error("Erro ao verificar o status do pagamento");
    }

    const status = response.data.pago ? "approved" : "pending";
    if (cotacao && transactionId) {
      trackCotacaoPaymentStatus(cotacao, transactionId, status);
    }
    return status;
  };

  return (
    <QuoteContext.Provider
      value={{
        cotacao,
        vehicleCotation,
        transportData,
        personalData,
        quotePrice,
        quoteModifiers,
        utmParameters,
        errorCotacao,
        cuponsDisponiveis,
        fetchCupons,
        applyCoupon,
        addAdditionals,
        addDiscount,
        updateVehicleCotation,
        updateTransportData,
        updatePersonalData,
        updateUtmParameters,
        clearUtmParameters,
        sendQuote,
        updateQuoteModifiers,
        fetchCotacaoById,
        updateVehicleData,
        updateModifierColeta,
        updateModifierEntrega,
        updateModifierBlindado,
        updateModifierOperante,
        updateModifierSeguro,
        updateModifierExpress,
        updateDateEntrega,
        updateCEPEntrega,
        updateCEPColeta,

        isContractSigned,

        clientInfoData,
        saveClientInfo,

        savePaymentData,
        paymentData,
        // paymentLocked,
        // setPaymentLocked,
        calculateValue,

        payWithCreditCard,
        payWithPix,
        payWithBoleto,
        checkPaymentStatus,
        transactionId,

        transaction,
        setTransaction,
        refetchTransaction,

        persistedBoletoData,
        persistedPixData,
        persistedCreditCardData,
        saveBoletoData,
        savePixData,
        saveCreditCardData,
        clearPersistedPaymentData,

        token,
        saveToken,
        getToken,
        clearToken,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
};

export const useQuoteContext = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error("useQuoteContext must be used within a QuoteProvider");
  }
  return context;
};
